import { MutableRefObject, forwardRef, useRef } from 'react';
import type { TextFieldProps } from '@mui/material';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import { CalendarAndClockIcon, CalendarIcon, ClockIcon } from '~/icons';

export enum PickerInputTypeEnum {
  DATE = 'date',
  TIME = 'time',
  DATETIME_LOCAL = 'datetime-local',
}

const PickerInput = forwardRef<HTMLInputElement, TextFieldProps>(({ type, value, ...props }, ref) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleInputRef = (instance: HTMLInputElement | null) => {
    inputRef.current = instance;

    if (!ref) return;

    if (typeof ref === 'function') {
      ref(instance);
    } else {
      (ref as MutableRefObject<HTMLInputElement | null>).current = instance;
    }
  };

  const handleOpenPicker = (event?: { isTrusted?: boolean }) => {
    if (!inputRef.current) return;

    const input = inputRef.current as HTMLInputElement & { showPicker?: () => void };

    if (event && event.isTrusted === false) return;

    input.focus();

    try {
      if (input.showPicker) {
        input.showPicker();
      } else {
        input.click();
      }
    } catch {
      input.click();
    }
  };

  const getIcon = () => {
    switch (type) {
      case PickerInputTypeEnum.TIME:
        return <ClockIcon />;
      case PickerInputTypeEnum.DATE:
        return <CalendarIcon />;
      default:
        return <CalendarAndClockIcon />;
    }
  };

  return (
    <TextField
      type={type}
      inputRef={handleInputRef}
      onClick={handleOpenPicker}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleOpenPicker}>{getIcon()}</IconButton>
            </InputAdornment>
          ),
        },
        htmlInput: {
          sx: {
            '&::-webkit-calendar-picker-indicator': { display: 'none' },
            '&::-webkit-inner-spin-button': { display: 'none' },
          },
        },
      }}
      {...props}
      value={value ?? ''}
    />
  );
});

export default PickerInput;
